import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Chip,
  useTheme,
  Tooltip,
  Button,
} from '@mui/material';
import { Send, Refresh, Clear, Chat } from '@mui/icons-material';
import { useEdvisor } from '../../contexts/EdvisorContext';
import ChatHistoryDropdown from './ChatHistoryDropdown';

interface EdvisorInputProps {
  disabled?: boolean;
}

const EdvisorInput: React.FC<EdvisorInputProps> = ({ disabled = false }) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  
  const { 
    sendMessage, 
    loading, 
    error, 
    retryLastMessage,
    isTyping,
    startNewChat
  } = useEdvisor();
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || loading || isTyping) return;

    await sendMessage(message.trim());
    setMessage('');
  };

  const handleSuggestionClick = async (suggestion: string) => {
    setMessage(suggestion);
    await sendMessage(suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleRetry = async () => {
    await retryLastMessage();
  };

  // Auto-focus input when not loading or when component mounts
  useEffect(() => {
    if (!loading && !isTyping && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, isTyping]);

  // Focus input when component first mounts
  useEffect(() => {
    if (inputRef.current) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        if (inputRef.current && !loading && !isTyping) {
          inputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // Empty dependency array means this runs only on mount

  return (
    <Box sx={{ p: 1, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Paper
            sx={{
              p: 1.5,
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ fontSize: '0.875rem' }}>
              {error}
            </Box>
            <Tooltip title="Retry last message">
              <IconButton 
                size="small" 
                onClick={handleRetry}
                sx={{ color: 'inherit' }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: 1,
          width: '100%',
          minWidth: 0, // Allow flex items to shrink below their content size
        }}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            placeholder={
              loading || isTyping 
                ? "Ed is thinking..." 
                : "Ask Ed about schools, programs, or admissions information..."
            }
            disabled={disabled || loading || isTyping}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 0, // Allow TextField to shrink
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                '&.Mui-focused': {
                  backgroundColor: theme.palette.background.paper,
                },
              },
            }}
          />
          
          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5,
            flexShrink: 0, // Prevent buttons from shrinking
          }}>
            <Tooltip title="Send message">
              <span>
                <IconButton
                  type="submit"
                  disabled={!message.trim() || loading || isTyping || disabled}
                  color="primary"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  <Send fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </form>

      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        mt: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
        width: '100%',
      }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<Chat />}
          onClick={startNewChat}
          sx={{ 
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            flexShrink: 0,
          }}
        >
          New Chat
        </Button>
        <Box sx={{ flexShrink: 0 }}>
          <ChatHistoryDropdown disabled={disabled || loading || isTyping} />
        </Box>
      </Box>

    </Box>
  );
};

export default EdvisorInput;
